import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, ShoppingCart, ExternalLink, Search, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/boards/data`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const money = (n) => (Number(n) || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
const STATUS = { active: 'bg-emerald-100 text-emerald-800', in_progress: 'bg-emerald-100 text-emerald-800', wip: 'bg-sky-100 text-sky-800', planning: 'bg-amber-100 text-amber-800', on_hold: 'bg-slate-200 text-slate-700', invoiced: 'bg-violet-100 text-violet-800', completed: 'bg-slate-200 text-slate-700' };

export function JobPicker({ onPick, autoFocus = true }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => axios.get(`${API}/jobs/search`, { ...auth(), params: { q } }).then(r => setRows(r.data.jobs || [])).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="flex flex-col h-full min-h-0" data-testid="job-picker">
      <div className="relative">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
        <input autoFocus={autoFocus} value={q} onChange={e => setQ(e.target.value)} placeholder="Find a job — number, customer, title…" data-testid="job-picker-search"
          className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
      <div className="mt-2 overflow-y-auto flex-1 min-h-0 divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white">
        {rows.map(j => (
          <button key={j.project_number} onClick={() => onPick(j)} data-testid={`job-pick-${j.project_number}`}
            className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-3">
            <span className="font-mono text-xs font-bold text-blue-700 w-16 shrink-0">{j.project_number}</span>
            <span className="text-sm text-slate-800 truncate flex-1">{j.client_name}{j.title ? ` — ${j.title}` : ''}</span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400">{(j.status || '').replace('_', ' ')}</span>
          </button>
        ))}
        {rows.length === 0 && <p className="text-xs text-slate-400 p-3">No jobs match.</p>}
      </div>
    </div>
  );
}

export default function JobTile({ config, onConfig, size }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [picking, setPicking] = useState(!config?.wo);
  const wo = config?.wo;

  useEffect(() => {
    if (!wo) return;
    let on = true;
    const load = () => axios.get(`${API}/job/${encodeURIComponent(wo)}`, auth()).then(r => on && (setD(r.data), setErr(''))).catch(e => on && setErr(e.response?.data?.detail || 'Could not load job'));
    load();
    const t = setInterval(load, 60000);
    return () => { on = false; clearInterval(t); };
  }, [wo]);

  if (picking || !wo) {
    return (
      <div className="p-3 h-full flex flex-col">
        <p className="text-xs text-slate-500 mb-2">Pick the job this tile should follow.</p>
        <JobPicker onPick={(j) => { onConfig({ wo: j.project_number }); setPicking(false); }} />
      </div>
    );
  }
  if (err) return <p className="p-4 text-sm text-red-600" data-testid="job-tile-error">{err}</p>;
  if (!d) return <div className="p-4 text-sm text-slate-400 animate-pulse">Loading {wo}…</div>;

  const pct = d.spent_pct;
  const barColor = pct == null ? 'bg-slate-300' : pct > 100 ? 'bg-red-500' : pct > 85 ? 'bg-amber-500' : 'bg-emerald-500';
  const compact = size.h < 5;
  return (
    <div className="p-3 h-full flex flex-col gap-2 text-slate-800" data-testid={`job-tile-${wo}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(`/projects/${encodeURIComponent(wo)}`)} className="font-mono text-base font-black text-blue-700 hover:underline" data-testid="job-tile-open">{wo}</button>
            {d.status && <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${STATUS[d.status] || 'bg-slate-100 text-slate-600'}`}>{d.status.replace('_', ' ')}</span>}
          </div>
          <p className="text-sm font-semibold truncate">{d.customer}{d.title ? <span className="text-slate-500 font-normal"> — {d.title}</span> : null}</p>
        </div>
        <button onClick={() => setPicking(true)} title="Change job" className="no-drag p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid="job-tile-change"><Pencil className="h-3.5 w-3.5" /></button>
      </div>

      <div className={`grid gap-2 ${compact ? 'grid-cols-3' : 'grid-cols-3'}`}>
        <Stat icon={Users} label="On site now" value={d.on_site?.length ?? 0} hint={(d.on_site || []).join(', ') || 'Nobody clocked in on this job right now'} accent={d.on_site?.length ? 'text-emerald-700' : 'text-slate-700'} />
        <Stat icon={Clock} label="Hours this wk" value={d.hours_week ?? '—'} hint={`${d.hours_total ?? 0} h total · shop ${d.shop_hours ?? 0} / field ${d.field_hours ?? 0}`} />
        <Stat icon={ShoppingCart} label="POs" value={d.po_count ?? 0} hint={money(d.po_total)} sub={money(d.po_total)} />
      </div>

      {!compact && (
        <div className="mt-auto">
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-slate-500">Budget {d.budget ? money(d.budget) : '— not set'}</span>
            {d.labour_visible && <span className="font-semibold">{money(d.spent)} spent{pct != null ? ` · ${pct}%` : ''}</span>}
          </div>
          <div className="h-2 mt-1 rounded-full bg-slate-200 overflow-hidden" title={pct != null ? `${pct}% of budget used (labour + consumables)` : 'No budget on the work order'}>
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pct || 0)}%` }} />
          </div>
          <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500">
            <span className="truncate">PM {d.pm || '—'}{d.customer_po ? ` · Cust PO ${d.customer_po}` : ''}</span>
            <button onClick={() => navigate(`/projects/${encodeURIComponent(wo)}`)} className="no-drag inline-flex items-center gap-1 hover:text-blue-700" data-testid="job-tile-open-link">Open job <ExternalLink className="h-3 w-3" /></button>
          </div>
          {d.last_note?.text && <p className="text-[11px] text-slate-600 mt-1 line-clamp-2 italic">“{d.last_note.text}”</p>}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, sub, accent = 'text-slate-800' }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5 min-w-0" title={hint}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500"><Icon className="h-3 w-3" />{label}</div>
      <div className={`text-xl font-black leading-tight ${accent}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 truncate">{sub}</div>}
    </div>
  );
}
